export type AnalyticsConsentState = 'unknown' | 'granted' | 'denied';

const CONSENT_STORAGE_KEY = 'ts_analytics_consent_v1';
const CONSENT_EVENT_NAME = 'ts-analytics-consent-updated';
const CONSENT_COOKIE_MAX_AGE = 60 * 60 * 24 * 180;

const readCookieState = (): AnalyticsConsentState => {
  if (typeof document === 'undefined') return 'unknown';

  const cookie = document.cookie
    .split('; ')
    .find((entry) => entry.startsWith(`${CONSENT_STORAGE_KEY}=`));

  const value = cookie?.split('=').slice(1).join('=');
  if (value === 'granted' || value === 'denied') {
    return value;
  }

  return 'unknown';
};

const writeCookieState = (state: Exclude<AnalyticsConsentState, 'unknown'>): void => {
  if (typeof document === 'undefined') return;
  document.cookie = `${CONSENT_STORAGE_KEY}=${state}; Max-Age=${CONSENT_COOKIE_MAX_AGE}; Path=/; SameSite=Lax`;
};

const clearCookieState = (): void => {
  if (typeof document === 'undefined') return;
  document.cookie = `${CONSENT_STORAGE_KEY}=; Max-Age=0; Path=/; SameSite=Lax`;
};

export class AnalyticsConsentService {
  static getState(): AnalyticsConsentState {
    if (typeof window === 'undefined') return 'unknown';

    const value = window.localStorage.getItem(CONSENT_STORAGE_KEY);
    if (value === 'granted' || value === 'denied') {
      writeCookieState(value);
      return value;
    }

    const cookieValue = readCookieState();
    if (cookieValue === 'granted' || cookieValue === 'denied') {
      window.localStorage.setItem(CONSENT_STORAGE_KEY, cookieValue);
      return cookieValue;
    }

    return 'unknown';
  }

  static hasDecision(): boolean {
    return this.getState() !== 'unknown';
  }

  static hasGranted(): boolean {
    return this.getState() === 'granted';
  }

  static setState(state: Exclude<AnalyticsConsentState, 'unknown'>): void {
    if (typeof window === 'undefined') return;

    window.localStorage.setItem(CONSENT_STORAGE_KEY, state);
    writeCookieState(state);
    window.dispatchEvent(
      new CustomEvent(CONSENT_EVENT_NAME, {
        detail: { state }
      })
    );
  }

  static clear(): void {
    if (typeof window === 'undefined') return;

    window.localStorage.removeItem(CONSENT_STORAGE_KEY);
    clearCookieState();
    window.dispatchEvent(
      new CustomEvent(CONSENT_EVENT_NAME, {
        detail: { state: 'unknown' }
      })
    );
  }

  static subscribe(listener: (state: AnalyticsConsentState) => void): () => void {
    if (typeof window === 'undefined') {
      return () => undefined;
    }

    const handler = (event: Event) => {
      const customEvent = event as CustomEvent<{ state?: AnalyticsConsentState }>;
      listener(customEvent.detail?.state || this.getState());
    };

    const storageHandler = (event: StorageEvent) => {
      if (event.key !== CONSENT_STORAGE_KEY) return;
      listener(this.getState());
    };

    window.addEventListener(CONSENT_EVENT_NAME, handler as EventListener);
    window.addEventListener('storage', storageHandler);
    return () => {
      window.removeEventListener(CONSENT_EVENT_NAME, handler as EventListener);
      window.removeEventListener('storage', storageHandler);
    };
  }
}
